import is from '@sindresorhus/is';
import type { NavigateFunction } from 'react-router';
import { z } from 'zod';
import type { StoreApi } from 'zustand';
import { createStore } from 'zustand';

import { type SkillProficiencyLevel, skillProficiencyLevelSchema } from '~/shared/data-contract/skill';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { languageCodeSchemaForJSA } from '~/shared/internationalization';

export type NavigatableSidepanelItem = {
    readonly id: string;
};

const itemSchema: z.ZodType<NavigatableSidepanelItem> = z.lazy(() =>
    z.object({
        id: z.string(),
        children: z.array(itemSchema).optional(),
    }),
);

const sidepanelSchema = z.object({
    architectureId: z.string().optional(),
    clusterId: z.string().optional(),
    detailType: z.literal('job-profile'),
    isArchived: z.boolean().optional(),
    itemType: z.enum(['job-level', 'job-role']),
    mode: z.enum(['edit', 'view']),
    preferredLanguage: languageCodeSchemaForJSA.optional(),
    profileId: z.string(),
    version: z.number().optional(),
});

const skillsSidepanelSchema = z.object({
    id: z.string(),
    availableItems: z.array(itemSchema).readonly().optional(),
    detailType: z.literal('skills'),
    preferredLanguage: languageCodeSchemaForJSA.optional(),
    proficiency: skillProficiencyLevelSchema.nullable().optional(),
});

const comparingSchema = z.object({
    id: z.string(),
    detailType: z.literal('comparing'),
    preferredLanguage: languageCodeSchemaForJSA.optional(),
    type: z.enum(['job-level', 'job-role']),
    version_a: z.string(),
    version_b: z.string(),
});

const familySidepanelSchema = z.object({
    detailType: z.literal('family'),
    familyId: z.string(),
    mode: z.enum(['edit', 'view']),
});

const clusterSidepanelSchema = z.object({
    clusterId: z.string(),
    detailType: z.literal('cluster'),
    familyId: z.string(),
    mode: z.enum(['edit', 'view']),
});

const changelogSidepanelSchema = z.object({
    detailType: z.literal('changelog'),
});

const archiveSidepanelSchema = z.object({
    detailType: z.literal('archive'),
});

export type JobProfileSidepanel = z.infer<typeof sidepanelSchema>;

export type SkillsSidepanel = z.infer<typeof skillsSidepanelSchema>;

export type ComparingSidepanel = z.infer<typeof comparingSchema>;

export type FamilySidepanel = z.infer<typeof familySidepanelSchema>;

export type ClusterSidepanel = z.infer<typeof clusterSidepanelSchema>;

export type ChangelogSidepanel = z.infer<typeof changelogSidepanelSchema>;

export type ArchiveSidepanel = z.infer<typeof archiveSidepanelSchema>;

export type Sidepanel =
    | ArchiveSidepanel
    | ChangelogSidepanel
    | ClusterSidepanel
    | ComparingSidepanel
    | FamilySidepanel
    | JobProfileSidepanel
    | SkillsSidepanel;

export type UrlNavigationStoreState = {
    readonly activeJobArchitectureId: string;
    readonly selectedClusterId: string | undefined;
    readonly selectedFamilyId: string | undefined;
    readonly selectedLevelId: string | undefined;
    readonly selectedRoleId: string | undefined;
    readonly sidepanels: readonly Sidepanel[];
};

export type UrlNavigationStoreActions = {
    readonly clearComparing: () => void;
    readonly clearSelection: () => void;
    readonly closeArchiveDetails: () => void;
    readonly closeChangelogDetails: () => void;
    readonly closeClusterDetails: () => void;
    readonly closeDetails: () => void;
    readonly closeFamilyDetails: () => void;
    readonly closeSidepanel: (types: readonly Sidepanel['detailType'][]) => void;
    readonly closeSkillsDetails: () => void;
    readonly hasSidepanelTypeOpen: (types: readonly Sidepanel['detailType'][]) => boolean;
    readonly initializeSelectionFromUrl: (searchParams: URLSearchParams) => void;
    readonly isLastActiveSegment: (segment: 'job-cluster' | 'job-family' | 'job-level' | 'job-role') => boolean;
    readonly navigateToArchitecture: (jobArchitectureId: string, tab?: string) => void;
    readonly openArchiveDetails: () => void;
    readonly openChangelogDetails: () => void;
    readonly openClusterDetails: (clusterId: string, familyId: string, mode: 'edit' | 'view') => void;
    readonly openFamilyDetails: (familyId: string, mode: 'edit' | 'view') => void;
    readonly openJobProfileDetails: (params: {
        readonly clusterId?: string;
        readonly isArchived?: boolean;
        readonly mode: 'edit' | 'view';
        readonly profileId: string;
        readonly type: 'job-level' | 'job-role';
        readonly version?: number;
    }) => void;
    readonly openSidepanel: (sidepanel: Sidepanel, replaces?: readonly Sidepanel['detailType'][]) => void;
    readonly openSkillsDetails: (
        id: string,
        proficiency?: SkillProficiencyLevel | null,
        availableItems?: readonly NavigatableSidepanelItem[],
        preferredLanguage?: LanguageCodeForJSA,
    ) => void;
    readonly setComparing: (comparing: ComparingSidepanel) => void;
    readonly setSelectedClusterId: (clusterId: string | undefined) => void;
    readonly setSelectedFamilyId: (familyId: string | undefined) => void;
    readonly setSelectedLevelId: (levelId: string | undefined, parentRoleId?: string) => void;
    readonly setSelectedRoleId: (roleId: string | undefined) => void;
    readonly updateJobProfileLanguage: (language: LanguageCodeForJSA) => void;
    readonly updateSkillsPanelIfOpen: (items: readonly NavigatableSidepanelItem[]) => void;
    readonly updateSkillsProficiencyIfOpen: (skillId: string, proficiency: SkillProficiencyLevel) => void;
};

export type UrlNavigationStore = UrlNavigationStoreActions & UrlNavigationStoreState;

export type UrlNavigationStoreDependencies = {
    readonly activeJobArchitectureId: string;
    readonly navigate: NavigateFunction;
    readonly updateSearchParams: (updater: (params: URLSearchParams) => void) => void;
};

const isValidUUID = (value: string | undefined): boolean => {
    return z.uuid().safeParse(value).success;
};

const getValidFamilyId = (familyId: string | undefined): string | undefined => {
    return isValidUUID(familyId) ? familyId : undefined;
};

const getValidClusterId = (clusterId: string | undefined, familyId: string | undefined): string | undefined => {
    const validFamilyId = getValidFamilyId(familyId);

    return !is.undefined(validFamilyId) && isValidUUID(clusterId) ? clusterId : undefined;
};

const getValidRoleId = (
    roleId: string | undefined,
    familyId: string | undefined,
    clusterId: string | undefined,
): string | undefined => {
    const validFamilyId = getValidFamilyId(familyId);
    const validClusterId = getValidClusterId(clusterId, familyId);

    return !is.undefined(validFamilyId) && !is.undefined(validClusterId) && isValidUUID(roleId) ? roleId : undefined;
};

const getValidLevelId = (
    levelId: string | undefined,
    roleId: string | undefined,
    familyId: string | undefined,
    clusterId: string | undefined,
): string | undefined => {
    const validFamilyId = getValidFamilyId(familyId);
    const validClusterId = getValidClusterId(clusterId, familyId);
    const validRoleId = getValidRoleId(roleId, familyId, clusterId);

    return !is.undefined(validFamilyId) &&
        !is.undefined(validClusterId) &&
        !is.undefined(validRoleId) &&
        isValidUUID(levelId)
        ? levelId
        : undefined;
};

const validateSidepanelParams = (searchParams: URLSearchParams): readonly Sidepanel[] => {
    const sidepanel = searchParams.get('details');
    const sidepanelProfileId = searchParams.get('details.profileId');
    const sidepanelClusterId = searchParams.get('details.clusterId');
    const sidepanelType = searchParams.get('details.type');
    const sidepanelMode = searchParams.get('details.mode');
    const sidepanelFamilyId = searchParams.get('details.familyId');
    const sidepanelSkillsId = searchParams.get('details.skills.id');
    const sidepanelSkills = searchParams.get('details.skills');
    const sidepanelSkillsPreferredLanguage = searchParams.get('details.skills.preferredLanguage');
    const sidepanelVersion = searchParams.get('details.version');
    const sidepanelIsArchived = searchParams.get('details.isArchived');
    const jobProfilePreferredLanguage = searchParams.get('details.preferredLanguage');

    const comparingVersionA = searchParams.get('comparing.version_a');
    const comparingVersionB = searchParams.get('comparing.version_b');
    const comparingId = searchParams.get('comparing.id');
    const comparingType = searchParams.get('comparing.type');
    const comparingPreferredLanguage = searchParams.get('comparing.preferredLanguage');

    const validSidepanel = sidepanelSchema.safeParse({
        clusterId: sidepanelClusterId ?? undefined,
        detailType: sidepanel,
        isArchived: sidepanelIsArchived === 'true',
        itemType: sidepanelType,
        mode: sidepanelMode ?? 'view',
        preferredLanguage: jobProfilePreferredLanguage ?? undefined,
        profileId: sidepanelProfileId,
        version: is.nonEmptyString(sidepanelVersion) ? Number.parseInt(sidepanelVersion, 10) : 1,
    });

    const familySidepanel = familySidepanelSchema.safeParse({
        detailType: sidepanel,
        familyId: sidepanelFamilyId,
        mode: sidepanelMode ?? 'view',
    });

    const clusterSidepanel = clusterSidepanelSchema.safeParse({
        clusterId: sidepanelClusterId,
        detailType: sidepanel,
        familyId: sidepanelFamilyId,
        mode: sidepanelMode ?? 'view',
    });

    const skillSidepanel = skillsSidepanelSchema.safeParse({
        id: sidepanelSkillsId,
        availableItems: undefined,
        detailType: sidepanelSkills,
        preferredLanguage: sidepanelSkillsPreferredLanguage ?? undefined,
    });

    const changelogParam = searchParams.get('details.changelog');
    const changelogSidepanel = changelogSidepanelSchema.safeParse({
        detailType: changelogParam,
    });

    const archiveParam = searchParams.get('details.archive');
    const archiveSidepanel = archiveSidepanelSchema.safeParse({
        detailType: archiveParam,
    });

    const validComparing = comparingSchema.safeParse({
        id: comparingId,
        detailType: 'comparing',
        preferredLanguage: comparingPreferredLanguage ?? undefined,
        type: comparingType,
        version_a: comparingVersionA,
        version_b: comparingVersionB,
    });

    const sidepanels = [
        validSidepanel,
        familySidepanel,
        clusterSidepanel,
        skillSidepanel,
        changelogSidepanel,
        archiveSidepanel,
        validComparing,
    ].filter((sidepanel) => sidepanel.success);

    return sidepanels.map((sidepanel) => sidepanel.data);
};

const validateSearchParams = (
    searchParams: URLSearchParams,
): Pick<
    UrlNavigationStoreState,
    'selectedClusterId' | 'selectedFamilyId' | 'selectedLevelId' | 'selectedRoleId' | 'sidepanels'
> => {
    const familyId = searchParams.get('family') ?? undefined;
    const clusterId = searchParams.get('cluster') ?? undefined;
    const roleId = searchParams.get('role') ?? undefined;
    const levelId = searchParams.get('level') ?? undefined;

    const validatedSidepanel = validateSidepanelParams(searchParams);

    const validFamilyId = getValidFamilyId(familyId);
    const validClusterId = getValidClusterId(clusterId, familyId);
    const validRoleId = getValidRoleId(roleId, familyId, clusterId);
    const validLevelId = getValidLevelId(levelId, roleId, familyId, clusterId);

    return {
        selectedClusterId: validClusterId,
        selectedFamilyId: validFamilyId,
        selectedLevelId: validLevelId,
        selectedRoleId: validRoleId,
        sidepanels: validatedSidepanel,
    };
};

const clearAllDetailsParams = (params: URLSearchParams): void => {
    params.delete('details');
    params.delete('details.profileId');
    params.delete('details.clusterId');
    params.delete('details.type');
    params.delete('details.mode');
    params.delete('details.familyId');
};

const sidepanelParamsToDelete: Record<Sidepanel['detailType'], readonly string[]> = {
    archive: ['details.archive'],
    changelog: ['details.changelog'],
    cluster: ['details', 'details.clusterId', 'details.familyId', 'details.type', 'details.mode'],
    comparing: [
        'comparing.version_a',
        'comparing.version_b',
        'comparing.id',
        'comparing.type',
        'comparing.preferredLanguage',
    ],
    family: ['details', 'details.familyId', 'details.type', 'details.mode'],
    'job-profile': [
        'details',
        'details.profileId',
        'details.clusterId',
        'details.type',
        'details.mode',
        'details.familyId',
        'details.isArchived',
        'details.version',
        'details.preferredLanguage',
    ],
    skills: ['details.skills', 'details.skills.id', 'details.skills.preferredLanguage'],
};

const clearSidepanelParams = (params: URLSearchParams, type: Sidepanel['detailType']): void => {
    const paramsToDelete = sidepanelParamsToDelete[type];
    // biome-ignore lint/suspicious/useIterableCallbackReturn: suppressed due to migration
    paramsToDelete.forEach((param) => params.delete(param));
};

export const createUrlNavigationStore = (
    dependencies: UrlNavigationStoreDependencies,
): StoreApi<UrlNavigationStore> => {
    const { activeJobArchitectureId, navigate, updateSearchParams } = dependencies;

    return createStore<UrlNavigationStore>((setState, getState) => ({
        activeJobArchitectureId,

        clearComparing: () => {
            getState().closeSidepanel(['comparing']);
        },

        clearSelection: () => {
            setState({
                selectedClusterId: undefined,
                selectedFamilyId: undefined,
                selectedLevelId: undefined,
                selectedRoleId: undefined,
            });
            updateSearchParams((params) => {
                params.delete('family');
                params.delete('cluster');
                params.delete('role');
                params.delete('level');
            });
        },

        closeArchiveDetails: () => {
            getState().closeSidepanel(['archive']);
        },

        closeChangelogDetails: () => {
            getState().closeSidepanel(['changelog']);
        },

        closeClusterDetails: () => {
            getState().closeSidepanel(['cluster']);
        },

        closeDetails: () => {
            getState().closeSidepanel([
                'job-profile',
                'family',
                'cluster',
                'skills',
                'comparing',
                'changelog',
                'archive',
            ]);
        },

        closeFamilyDetails: () => {
            getState().closeSidepanel(['family']);
        },

        closeSidepanel: (types) => {
            updateSearchParams((params) => {
                for (const type of types) {
                    clearSidepanelParams(params, type);
                }
            });

            setState({
                sidepanels: getState().sidepanels.filter((panel) => !types.includes(panel.detailType)),
            });
        },

        closeSkillsDetails: () => {
            getState().closeSidepanel(['skills']);
        },

        comparing: undefined,

        hasSidepanelTypeOpen: (types) => {
            return getState().sidepanels.some((panel) => types.includes(panel.detailType));
        },

        initializeSelectionFromUrl: (searchParams) => {
            const validatedParams = validateSearchParams(searchParams);

            setState({
                ...validatedParams,
            });
        },

        isLastActiveSegment: (segment: 'job-cluster' | 'job-family' | 'job-level' | 'job-role') => {
            const state = getState();

            if (is.nonEmptyString(state.selectedLevelId)) {
                return segment === 'job-level';
            }

            if (is.nonEmptyString(state.selectedRoleId)) {
                return segment === 'job-role';
            }

            if (is.nonEmptyString(state.selectedClusterId)) {
                return segment === 'job-cluster';
            }

            if (is.nonEmptyString(state.selectedFamilyId)) {
                return segment === 'job-family';
            }

            return false;
        },

        navigateToArchitecture: (architectureId, tab) => {
            const currentTab = tab ?? 'overview';

            setState({
                activeJobArchitectureId: architectureId,
                selectedClusterId: undefined,
                selectedFamilyId: undefined,
                selectedLevelId: undefined,
                selectedRoleId: undefined,
            });

            navigate(`/job-architecture-v2/${architectureId}/${currentTab}`);

            updateSearchParams((params) => {
                params.delete('family');
                params.delete('cluster');
                params.delete('role');
                params.delete('level');
            });
        },

        openArchiveDetails: () => {
            getState().openSidepanel({
                detailType: 'archive',
            });
        },

        openChangelogDetails: () => {
            getState().openSidepanel({
                detailType: 'changelog',
            });
        },

        openClusterDetails: (clusterId: string, familyId: string, mode: 'edit' | 'view') => {
            getState().openSidepanel(
                {
                    clusterId,
                    detailType: 'cluster',
                    familyId,
                    mode,
                },
                ['job-profile', 'family'],
            );
        },

        openFamilyDetails: (familyId: string, mode: 'edit' | 'view') => {
            getState().openSidepanel(
                {
                    detailType: 'family',
                    familyId,
                    mode,
                },
                ['job-profile', 'cluster'],
            );
        },

        openJobProfileDetails: ({ clusterId, isArchived, mode, profileId, type, version }) => {
            getState().openSidepanel(
                {
                    clusterId,
                    detailType: 'job-profile',
                    isArchived,
                    itemType: type,
                    mode,
                    profileId,
                    version,
                },
                ['family', 'cluster', 'skills'],
            );
        },

        openSidepanel: (sidepanel, replaces = []) => {
            if (replaces.length > 0) {
                getState().closeSidepanel(replaces);
            }

            // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: suppressed due to migration
            updateSearchParams((params) => {
                switch (sidepanel.detailType) {
                    case 'job-profile': {
                        clearAllDetailsParams(params);
                        params.set('details', 'job-profile');
                        params.set('details.profileId', sidepanel.profileId);
                        if (!is.undefined(sidepanel.clusterId)) {
                            params.set('details.clusterId', sidepanel.clusterId);
                        }
                        params.set('details.mode', sidepanel.mode);
                        params.set('details.type', sidepanel.itemType);
                        if (!is.undefined(sidepanel.version)) {
                            params.set('details.version', sidepanel.version.toString());
                        }
                        if (sidepanel.isArchived === true) {
                            params.set('details.isArchived', 'true');
                        }
                        if (is.nonEmptyString(sidepanel.preferredLanguage)) {
                            params.set('details.preferredLanguage', sidepanel.preferredLanguage);
                        }
                        break;
                    }
                    case 'family': {
                        clearAllDetailsParams(params);
                        params.set('details', 'family');
                        params.set('details.familyId', sidepanel.familyId);
                        params.set('details.mode', sidepanel.mode);
                        params.set('details.type', 'family');
                        break;
                    }
                    case 'cluster': {
                        clearAllDetailsParams(params);
                        params.set('details', 'cluster');
                        params.set('details.clusterId', sidepanel.clusterId);
                        params.set('details.familyId', sidepanel.familyId);
                        params.set('details.mode', sidepanel.mode);
                        params.set('details.type', 'cluster');
                        break;
                    }
                    case 'skills': {
                        params.set('details.skills', 'skills');
                        params.set('details.skills.proficiency', sidepanel.proficiency?.toString() ?? '');
                        params.set('details.skills.id', sidepanel.id);
                        if (is.nonEmptyString(sidepanel.preferredLanguage)) {
                            params.set('details.skills.preferredLanguage', sidepanel.preferredLanguage);
                        }
                        break;
                    }
                    case 'comparing': {
                        params.set('comparing.id', sidepanel.id);
                        params.set('comparing.type', sidepanel.type);
                        params.set('comparing.version_a', sidepanel.version_a);
                        params.set('comparing.version_b', sidepanel.version_b);
                        params.set('comparing.detailType', 'comparing');
                        if (is.nonEmptyString(sidepanel.preferredLanguage)) {
                            params.set('comparing.preferredLanguage', sidepanel.preferredLanguage);
                        }
                        break;
                    }
                    case 'changelog': {
                        params.set('details.changelog', 'changelog');
                        break;
                    }
                    case 'archive': {
                        params.set('details.archive', 'archive');
                        break;
                    }
                }
            });

            const sidepanels = getState().sidepanels;
            const alreadyOpen = sidepanels.find((panel) => panel.detailType === sidepanel.detailType);

            if (is.undefined(alreadyOpen)) {
                setState({
                    sidepanels: [...sidepanels, sidepanel],
                });
            } else {
                setState({
                    sidepanels: sidepanels.map((panel) =>
                        panel.detailType === sidepanel.detailType ? sidepanel : panel,
                    ),
                });
            }
        },

        openSkillsDetails: (id, proficiency, availableItems, preferredLanguage) => {
            getState().openSidepanel({
                id,
                availableItems,
                detailType: 'skills',
                preferredLanguage,
                proficiency,
            });
        },

        selectedClusterId: undefined,
        selectedFamilyId: undefined,
        selectedLevelId: undefined,
        selectedRoleId: undefined,

        setComparing: (comparing) => {
            getState().openSidepanel(comparing);
        },

        setSelectedClusterId: (clusterId) => {
            setState({
                selectedClusterId: clusterId,
                selectedLevelId: undefined,
                selectedRoleId: undefined,
            });
            updateSearchParams((params) => {
                const state = getState();

                params.delete('family');
                params.delete('cluster');
                params.delete('role');
                params.delete('level');

                if (is.nonEmptyString(state.selectedFamilyId)) {
                    params.set('family', state.selectedFamilyId);
                }
                if (is.nonEmptyString(clusterId)) {
                    params.set('cluster', clusterId);
                }
            });
        },

        setSelectedFamilyId: (familyId) => {
            setState({
                selectedClusterId: undefined,
                selectedFamilyId: familyId,
                selectedLevelId: undefined,
                selectedRoleId: undefined,
            });
            updateSearchParams((params) => {
                params.delete('family');
                params.delete('cluster');
                params.delete('role');
                params.delete('level');

                if (is.nonEmptyString(familyId)) {
                    params.set('family', familyId);
                }
            });
        },

        setSelectedLevelId: (levelId, parentRoleId) => {
            const state = getState();
            const roleToUse = parentRoleId ?? state.selectedRoleId;

            if (is.undefined(roleToUse)) {
                return;
            }

            setState({
                selectedLevelId: levelId,
                selectedRoleId: roleToUse,
            });
            // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: suppressed due to migration
            updateSearchParams((params) => {
                const currentState = getState();

                params.delete('family');
                params.delete('cluster');
                params.delete('role');
                params.delete('level');

                if (is.nonEmptyString(currentState.selectedFamilyId)) {
                    params.set('family', currentState.selectedFamilyId);
                }
                if (is.nonEmptyString(currentState.selectedClusterId)) {
                    params.set('cluster', currentState.selectedClusterId);
                }
                if (is.nonEmptyString(roleToUse)) {
                    params.set('role', roleToUse);
                }
                if (is.nonEmptyString(levelId)) {
                    params.set('level', levelId);
                }
            });
        },

        setSelectedRoleId: (roleId) => {
            setState({
                selectedLevelId: undefined,
                selectedRoleId: roleId,
            });
            updateSearchParams((params) => {
                const state = getState();

                params.delete('family');
                params.delete('cluster');
                params.delete('role');
                params.delete('level');

                if (is.nonEmptyString(state.selectedFamilyId)) {
                    params.set('family', state.selectedFamilyId);
                }
                if (is.nonEmptyString(state.selectedClusterId)) {
                    params.set('cluster', state.selectedClusterId);
                }
                if (is.nonEmptyString(roleId)) {
                    params.set('role', roleId);
                }
            });
        },
        sidepanels: [],

        updateJobProfileLanguage: (language) => {
            const sidepanels = getState().sidepanels;
            const jobProfilePanel = sidepanels.find((panel) => panel.detailType === 'job-profile');

            if (!is.undefined(jobProfilePanel)) {
                updateSearchParams((params) => {
                    params.set('details.preferredLanguage', language);
                });

                setState({
                    sidepanels: sidepanels.map((panel) =>
                        panel.detailType === 'job-profile' ? { ...panel, preferredLanguage: language } : panel,
                    ),
                });
            }
        },

        updateSkillsPanelIfOpen: (items) => {
            const state = getState();
            const skillsPanel = state.sidepanels.find((panel) => panel.detailType === 'skills');

            if (!is.undefined(skillsPanel)) {
                setState({
                    sidepanels: state.sidepanels.map((panel) =>
                        panel.detailType === 'skills' ? { ...panel, availableItems: items } : panel,
                    ),
                });
            }
        },

        updateSkillsProficiencyIfOpen: (skillId, proficiency) => {
            const state = getState();
            const skillsPanel = state.sidepanels.find((panel) => panel.detailType === 'skills');

            if (!is.undefined(skillsPanel) && skillsPanel.id === skillId) {
                updateSearchParams((params) => {
                    params.set('details.skills.proficiency', proficiency.toString());
                });

                setState({
                    sidepanels: state.sidepanels.map((panel) =>
                        panel.detailType === 'skills' ? { ...panel, proficiency } : panel,
                    ),
                });
            }
        },
    }));
};
